/**
 * The hull, as an outline somebody outside the renderer can draw.
 *
 * This exists for the same reason `src/captain/portrait.ts` exists, and it is deliberately
 * the same shape of answer. A captain is a seed; `portrait.ts` turns the appearance half of
 * that seed into a buffer and hands it back, so `src/ui/captain-card.ts` can blit a face
 * without importing one line of `src/render/**`. A ship is the same seed plus a build, and
 * this turns that pair into ONE closed ring of numbers, so `src/ui/hull-preview.ts` can
 * stroke a hull without importing one line of `src/render/**` either.
 *
 * Why a module rather than a UI-side copy of the maths: AMD-43 spent 20,000 seeds proving
 * the generated hulls are still §12 mantas and still 3.3× further from every enemy than two
 * shipped enemies are from each other, and AMD-50 spent twelve integers proving the build
 * moves ~32% of the shape variance. A second implementation of `hullOutline` in `src/ui`
 * would inherit neither proof and would start drifting on the first parameter tweak. There
 * is exactly one hull generator; this is a view onto it, not a fork of it.
 *
 * Why the numbers rather than the pixels: the portrait's compositor has to live outside the
 * UI because the thing it composites IS the renderer — `crt.ts` is a line-for-line mirror of
 * the shipped post chain, and a UI-side copy would go stale the first time the tube was
 * retuned. A hull is not that. Its whole treatment is a fill, a 2 px rim and a halo, all
 * three of which are Canvas2D one-liners that belong next to the DOM they are drawn into
 * and next to `src/styles/tokens.css`, which is where the colour they are drawn in lives.
 * So the split lands at the last place the two layers genuinely disagree: geometry here,
 * paint in the UI.
 *
 * Nothing here is per-frame. A preview changes when the seed changes or the build changes,
 * which on the loadout screen is a keystroke and a tap.
 */

import { hullParamsForForm, hullParamsForSeed, markedOutline, type ShipFormView } from '../render/ship';

export type { ShipFormView } from '../render/ship';

/**
 * The hull this run seed and this build fly together, as one closed ring, normalised so the
 * furthest vertex sits at radius 1. Flat `[x0, y0, x1, y1, …]`, nose-first along +x,
 * counter-clockwise, marks already spliced in.
 *
 * Normalised here rather than by the caller for the same reason `buildPlayerGeometry`
 * normalises: AMD-50 expresses "small" as silhouette AREA inside a fixed footprint, not as
 * a smaller ship, so a defence build must fill more of the same box rather than draw bigger.
 * A caller that fitted the ring to its own bounding box would undo exactly that and every
 * hull would come out the same size — which is the one comparison the loadout screen is for.
 *
 * Pure and total. Allocates one `Float32Array` per call and holds nothing.
 */
export function previewOutline(runSeed: number, form: ShipFormView | null): Float32Array {
  const p = form === null ? hullParamsForSeed(runSeed) : hullParamsForForm(runSeed, form);
  const ring = form === null ? markedOutline(p, EMPTY_FORM) : markedOutline(p, form);
  let max = 0;
  for (let i = 0; i < ring.length; i += 2) {
    const r = Math.hypot(ring[i], ring[i + 1]);
    if (r > max) max = r;
  }
  const k = max > 0 ? 1 / max : 1;
  const out = new Float32Array(ring.length);
  for (let i = 0; i < ring.length; i++) out[i] = ring[i] * k;
  return out;
}

/** A build that owns nothing: exactly 4/4/4, no marks. The §12 reference read. */
export const EMPTY_FORM: ShipFormView = {
  points: [4, 4, 4],
  slotGlyph: [-1, -1, -1, -1],
  slotEvolved: [0, 0, 0, 0],
  familyRank: [0, 0, 0, 0],
  bites: 0,
  biteLevel: 0,
  killSteps: 0,
  signature: 0,
};
